/**
 * Startup requests.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md tasks 6.1, 6.2, 6.4, 6.5
 */
import type {
  ChangeStageRequest,
  CheckDuplicatesQuery,
  CreateStartupRequest,
  DuplicateMatch,
  ListStartupsQuery,
  Scorecard,
  SaveScorecardRequest,
  StartupSummary,
  UpdateStartupRequest,
} from '@nexus/contracts';

import { api } from '../../../lib/api-client';
import type { PageMeta } from '../../../lib/pagination';

/** A page of startups. */
export interface StartupPage {
  data: StartupSummary[];
  meta: PageMeta;
}

/** A startup with the exit-criteria checklist for its current stage. */
export interface StartupDetail {
  startup: StartupSummary & Record<string, unknown>;
  checklist: { key: string; label: string; isMet: boolean }[];
}

/** The result of a stage change, including any acknowledged warnings. */
export interface StageChangeResult {
  startup: StartupSummary;
  warnings: string[];
}

export async function listStartups(query: ListStartupsQuery): Promise<StartupPage> {
  return api.get<StartupPage>('/startups', { query: query });
}

export async function getStartup(id: string): Promise<StartupDetail> {
  return api.get<StartupDetail>(`/startups/${id}`);
}

export async function createStartup(body: CreateStartupRequest): Promise<StartupSummary> {
  return api.post<StartupSummary>('/startups', body);
}

/** Probable duplicates of a name and/or website (F14). */
export async function checkDuplicates(
  query: CheckDuplicatesQuery,
): Promise<{ duplicates: DuplicateMatch[] }> {
  return api.get<{ duplicates: DuplicateMatch[] }>('/startups/duplicates', { query });
}

export async function updateStartup(
  id: string,
  body: UpdateStartupRequest,
): Promise<StartupSummary> {
  return api.patch<StartupSummary>(`/startups/${id}`, body);
}

export async function changeStage(
  id: string,
  body: ChangeStageRequest,
): Promise<StageChangeResult> {
  return api.patch<StageChangeResult>(`/startups/${id}/stage`, body);
}

export async function getScorecard(startupId: string): Promise<Scorecard> {
  return api.get<Scorecard>(`/startups/${startupId}/scorecard`);
}

export async function saveScorecard(
  startupId: string,
  body: SaveScorecardRequest,
): Promise<Scorecard> {
  return api.put<Scorecard>(`/startups/${startupId}/scorecard`, body);
}
